import type { SxProps } from "@mui/material";
import type { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import type { TimeSlot } from "@/downloads/downloadDetailedDayView";
import { trackErrorEvent } from "@/utils/analytics";
import { NotificationType, type NurseFullRole, type NurseInShift, type OpenShiftRequest, type ReplaceShiftRequest } from "@/utils/models";
import type { MultiSortOption, SortingState } from "@/utils/multiColumnSorting";

type DayViewShift = {
	specificType: "shift";
	value: NurseInShift & { isOptimisticLoading?: boolean; isBeingMoved?: boolean };
	normalizedStartTime: DateTime;
	normalizedEndTime: DateTime;
};

export type DayViewGap = {
	specificType: "gap";
	value: { timeSlot: TimeSlot; gapIdx: string; postedRequestId?: string };
	normalizedStartTime: DateTime;
	normalizedEndTime: DateTime;
};

export type DayViewRowSlot = {
	start: DateTime;
	end: DateTime;
	/**
	 * Array shifts and gaps - each day view slot represent a horizontal timeline inside the DayViewGapsRow
	 */
	shiftsAndGaps: (DayViewGap | DayViewShift)[];
};

type DayViewGapsRow = {
	locationName: string;
	isMissingConfig: boolean;
	roles: (NurseFullRole | "all")[];
	jobId?: string;
	/**
	 * Each row is a timeline inside the location+roles
	 */
	rows: DayViewRowSlot[];
};

export const getDayViewGapRowKey = (locationName: string, roles: (NurseFullRole | "all")[], jobId?: string) => {
	const rolesSegment = jobId ? "" : [...roles].sort().join(",");
	const base = `${locationName}__${rolesSegment}`;
	return jobId ? `${base}__${jobId}` : base;
};

const MINUTES_PER_DAY = 1440;
// A posted shift and a gap match when their start AND end are within this many minutes of each other
// (source or target off by up to this much still counts) — not strict equality.
const MATCH_TOLERANCE_MIN = 30;

const timeSlotStartMin = (ts: TimeSlot) => ts.start.hour * 60 + ts.start.minute;
const timeSlotEndMin = (ts: TimeSlot) => ts.end.hour * 60 + ts.end.minute;
const requestStartMin = (req: OpenShiftRequest) => req.message.shift_start.hour * 60 + req.message.shift_start.minute;
const requestEndMin = (req: OpenShiftRequest) => req.message.shift_end.hour * 60 + req.message.shift_end.minute;

/** Circular minute-of-day difference so times near midnight compare correctly (23:50 vs 00:10 = 20, not 1420). */
const minuteOfDayDiff = (a: number, b: number): number => {
	const raw = Math.abs(a - b) % MINUTES_PER_DAY;
	return Math.min(raw, MINUTES_PER_DAY - raw);
};

/**
 * Signature that groups missing gaps sharing the same time window + job/role — deliberately IGNORING
 * location. Under `pickup_by_location` OFF a posted shift is dept-wide (no location), so one post can
 * cover any location's gap of the same shape; grouping by this signature lets us count all of them
 * together. The discriminator is the job when JobManagement is on (mirrors `getDayViewGapRowKey`), the
 * sorted role-set otherwise. Both the coverage map (below) and the render sites use this exact key.
 */
export const getGapSignatureKey = (timeSlot: TimeSlot, jobManagementEnabled: boolean): string => {
	const discriminator = jobManagementEnabled ? (timeSlot.jobId ?? "") : [...timeSlot.roles].sort().join(",");
	return `${timeSlotStartMin(timeSlot)}__${timeSlotEndMin(timeSlot)}__${discriminator}`;
};

export type GapPostedCoverage = {
	/** Y — number of missing gaps sharing this signature (across locations). */
	missingCount: number;
	/** X — open capacity summed over posted shifts that match this signature. */
	openCapacity: number;
	/** The posted shift to scroll/pulse when the coverage label is clicked (first match), or null. */
	targetRequestId: string | null;
};

type GapCoverageAccumulator = {
	missingCount: number;
	startMin: number;
	endMin: number;
	sectionJobId: string | null;
	/** Union of the group's gap roles (excluding "all"); empty ⇒ matches any role. */
	roles: Set<NurseFullRole>;
};

/**
 * A posted shift covers a gap group when it is an open-shift request, its start AND end are each within
 * MATCH_TOLERANCE_MIN of the gap's, and the job (JobManagement on) or role (off) lines up. Location is
 * ignored — under `pickup_by_location` OFF a posted shift is dept-wide, so it covers the same-shaped gap
 * in any location. Typed as a predicate so callers narrow to OpenShiftRequest after it returns true.
 */
const requestMatchesSignature = (
	req: OpenShiftRequest | ReplaceShiftRequest,
	acc: GapCoverageAccumulator,
	jobManagementEnabled: boolean,
): req is OpenShiftRequest => {
	if (req.request_type !== NotificationType.OpenShift || !("needed_nurses" in req)) return false;
	if (minuteOfDayDiff(requestStartMin(req), acc.startMin) > MATCH_TOLERANCE_MIN) return false;
	if (minuteOfDayDiff(requestEndMin(req), acc.endMin) > MATCH_TOLERANCE_MIN) return false;
	if (jobManagementEnabled) return (req.job_id ?? null) === acc.sectionJobId;
	if (acc.roles.size === 0) return true; // an "all"-role gap accepts any posted role
	return (req.message.roles as NurseFullRole[]).some((role) => acc.roles.has(role));
};

/**
 * Build the per-signature posted-shift coverage used by the Day view when `pickup_by_location` is OFF.
 * A dept-wide post is counted against every gap group whose exact time + job/role it matches (many-to-many,
 * NOT consumed like the matcher). Efficient: one pass to group gaps, then for each signature a scan over posts.
 *
 * keys are made from {@link getGapSignatureKey}
 */
export function computeGapPostedCoverageBySignature({
	gapTimeSlots,
	postedRequests,
	jobManagementEnabled,
}: {
	gapTimeSlots: TimeSlot[];
	postedRequests: (OpenShiftRequest | ReplaceShiftRequest)[];
	jobManagementEnabled: boolean;
}): Map<string, GapPostedCoverage> {
	const accumulators = new Map<string, GapCoverageAccumulator>();
	for (const ts of gapTimeSlots) {
		const key = getGapSignatureKey(ts, jobManagementEnabled);
		let acc = accumulators.get(key);
		if (!acc) {
			acc = {
				missingCount: 0,
				startMin: timeSlotStartMin(ts),
				endMin: timeSlotEndMin(ts),
				sectionJobId: jobManagementEnabled ? ts.jobId : null,
				roles: new Set<NurseFullRole>(),
			};
			accumulators.set(key, acc);
		}
		acc.missingCount += 1;
		for (const role of ts.roles) {
			if (role !== "all") acc.roles.add(role);
		}
	}

	const coverage = new Map<string, GapPostedCoverage>();
	for (const [key, acc] of accumulators) {
		let openCapacity = 0;
		let targetRequestId: string | null = null;
		for (const req of postedRequests) {
			if (!requestMatchesSignature(req, acc, jobManagementEnabled)) continue;
			// `needed_nurses` IS the open capacity — the backend decrements it as nurses are approved
			openCapacity += req.needed_nurses;
			if (targetRequestId === null) targetRequestId = req.id;
		}
		coverage.set(key, { missingCount: acc.missingCount, openCapacity, targetRequestId });
	}
	return coverage;
}

export const filterNoLocationSlotsByJob = (rows: DayViewRowSlot[], selectedJobFilters: string[]): DayViewRowSlot[] =>
	rows
		.map((slot) => ({
			...slot,
			shiftsAndGaps: slot.shiftsAndGaps.filter((item) => item.specificType === "shift" && !!item.value.jobId && selectedJobFilters.includes(item.value.jobId)),
		}))
		.filter((slot) => slot.shiftsAndGaps.length > 0);

export type RowNameAndRoles = {
	locationName: string;
	roles: (NurseFullRole | "all")[];
	jobId?: string;
};

export const defaultSortStateDailyGapView: SortingState<RowNameAndRoles>[] = [
	{
		sortColumns: ["locationName"],
		sortOrder: "asc",
		label: "Location",
	},
	{
		sortColumns: ["roles"],
		sortOrder: "asc",
		label: "Roles",
	},
];

export const sortingOptions: MultiSortOption<RowNameAndRoles>[] = [
	{
		label: "Location",
		sortColumns: ["locationName"],
	},
	{
		label: "Roles",
		sortColumns: ["roles"],
	},
];

export const getRowNameAndRolesFromKey = (key: string): RowNameAndRoles => {
	const [locationName, rolesString, jobId] = key.split("__");
	const roles = (rolesString ? rolesString.split(",") : ["all"]) as (NurseFullRole | "all")[];
	return { locationName, roles, jobId };
};
export const noLocationString = "No Location";
export const postedShiftString = "Posted Shift";
export const replaceRequestString = "Replace Request";
export const emptyLocationKey = getDayViewGapRowKey(noLocationString, ["all"]);

export type CalculatedDayViewGaps = Record<string, DayViewGapsRow>;

type DayViewPostedRequestElement = {
	value: OpenShiftRequest | ReplaceShiftRequest;
	normalizedStartTime: DateTime;
	normalizedEndTime: DateTime;
};

type DayViewPostedRequestRowSlot = {
	start: DateTime;
	end: DateTime;
	requests: Array<DayViewPostedRequestElement>;
};

type DayViewPostedRequest = {
	roles: (NurseFullRole | "all")[];
	jobId?: string;
	rows: DayViewPostedRequestRowSlot[];
};
export type CalculatedDayViewPostedRequests = Record<string, DayViewPostedRequest>;

export const normalizeEnd = (start: DateTime, end: DateTime, includingSame = true) => {
	return (includingSame ? end <= start : end < start) ? end.plus({ days: 1 }) : end;
};

export const startAndEndTimeIncComparator = (a: { start: DateTime; end: DateTime }, b: { start: DateTime; end: DateTime }) => {
	if (!a.start?.isValid || !b.start?.isValid) return 0;
	const aStart = a.start.valueOf();
	const bStart = b.start.valueOf();
	if (aStart !== bStart) return aStart - bStart;
	// If starts are equal, compare (possibly normalized) ends
	if (!a.end?.isValid || !b.end?.isValid) return 0;
	const aEnd = normalizeEnd(a.end, a.end).valueOf();
	const bEnd = normalizeEnd(b.end, b.end).valueOf();
	return aEnd - bEnd;
};

/**
 * Normalize slot times so they fall within the 24h timeline window [dayStart, dayStart+24h].
 * Times that are on or before the dayStart boundary are shifted forward by 1 day so they
 * appear in the correct position on the timeline (e.g. 4PM with dayStart 8PM → next-day 4PM).
 */
export function normalizeStartEndWithStartOfDay({ date, start, end, startHour }: { date: DateTime; start: DateTime; end: DateTime; startHour: number }) {
	if (!start.isValid || !end.isValid || !date.isValid) {
		trackErrorEvent("day_view_gaps_normalizeStartEnd_invalid_start_or_end_or_startOfDay", {
			start: start?.toISO(),
			end: end?.toISO(),
			startOfDay: date?.toISO(),
		});
		return { start, end };
	}
	const startOfDay = date.startOf("day").set({ hour: startHour });
	const normalizedStart = start <= startOfDay ? normalizeEnd(startOfDay, start, false) : start;
	const normalizedEnd = normalizeEnd(normalizedStart, end);
	return { start: normalizedStart, end: normalizedEnd };
}

/**
 * Insert or update a shift/gap slot in the given DayViewGapsRow.
 * Function will try to insert to the most upper inner row possible without overlapping existing shifts/gaps, in case we overlap move to a new inner row.
 *
 * You should either provide a nurse (to add a shift) or a missingSlot (to add a gap).
 * @param row - The DayViewGapsRow to update (find it by key)
 * @param start - Start time of the new slot
 * @param end - End time of the new slot
 * @param nurse - NurseInShift to add to the slot
 * @param missingSlot - TimeSlot representing a missing slot
 * @param date - The date for context
 */
export function upsertSlotIntoDayViewRows({
	start,
	end,
	missingSlot,
	nurse,
	row,
	date,
	postedRequestId,
	startHour,
}: {
	row: DayViewGapsRow;
	start: DateTime;
	end: DateTime;
	nurse?: NurseInShift;
	missingSlot?: TimeSlot;
	postedRequestId?: string;
	date: DateTime;
	startHour: number;
}) {
	if (!nurse && !missingSlot) {
		trackErrorEvent("day_view_gaps_upsertSlot_no_nurse_or_missingSlot_provided", {
			row,
			start: start.toISO(),
			end: end.toISO(),
		});
		return;
	}
	if (!row) {
		trackErrorEvent("day_view_gaps_upsertSlot_no_row_provided", {
			start: start.toISO(),
			end: end.toISO(),
			nurse,
			missingSlot,
		});
		return;
	}
	if (!start?.isValid || !end?.isValid) {
		trackErrorEvent("day_view_gaps_upsertSlot_invalid_start_or_end", {
			row,
			start: start.toISO(),
			end: end.toISO(),
			nurse,
			missingSlot,
		});
		return;
	}
	if (!date || !date.isValid) {
		trackErrorEvent("day_view_gaps_upsertSlot_invalid_date", {
			row,
			start: start.toISO(),
			end: end.toISO(),
			nurse,
			missingSlot,
			date: date?.toISO(),
		});
		return;
	}
	const normalizedSlotTimesInRelationToDayStart = normalizeStartEndWithStartOfDay({ date, start, end, startHour });

	const newElement: DayViewGap | DayViewShift = nurse
		? {
				specificType: "shift",
				value: nurse,
				normalizedStartTime: normalizedSlotTimesInRelationToDayStart.start,
				normalizedEndTime: normalizedSlotTimesInRelationToDayStart.end,
			}
		: {
				specificType: "gap",
				value: { timeSlot: missingSlot!, gapIdx: uuidv4(), postedRequestId },
				normalizedStartTime: normalizedSlotTimesInRelationToDayStart.start,
				normalizedEndTime: normalizedSlotTimesInRelationToDayStart.end,
			};

	const noOverlaps: number[] = [];
	for (let i = 0; i < row.rows.length; i++) {
		const currentRowSlot = row.rows[i];
		const overlapsSomeElement = currentRowSlot.shiftsAndGaps.some((existingShiftOrGap) => {
			const hasNoOverlap =
				normalizedSlotTimesInRelationToDayStart.start >= existingShiftOrGap.normalizedEndTime ||
				normalizedSlotTimesInRelationToDayStart.end <= existingShiftOrGap.normalizedStartTime;
			if (hasNoOverlap) return false;
			// There is overlap - only treat as conflict if >= threshold
			const overlapStart =
				normalizedSlotTimesInRelationToDayStart.start > existingShiftOrGap.normalizedStartTime
					? normalizedSlotTimesInRelationToDayStart.start
					: existingShiftOrGap.normalizedStartTime;
			const overlapEnd =
				normalizedSlotTimesInRelationToDayStart.end < existingShiftOrGap.normalizedEndTime
					? normalizedSlotTimesInRelationToDayStart.end
					: existingShiftOrGap.normalizedEndTime;
			const overlapMinutes = overlapEnd.diff(overlapStart, "minutes").as("minutes");
			return overlapMinutes > MAX_OVERLAP_MINUTES_FOR_SAME_ROW;
		});
		if (!overlapsSomeElement) {
			noOverlaps.push(i);
		}
	}

	if (noOverlaps.length === 0) {
		//all rows overlap, need to create a new row
		const newRowSlot: DayViewRowSlot = {
			start: normalizedSlotTimesInRelationToDayStart.start,
			end: normalizedSlotTimesInRelationToDayStart.end,
			shiftsAndGaps: [newElement],
		};
		row.rows.push(newRowSlot);
	} else {
		//insert into the first non-overlapping row
		const targetRowIdx = noOverlaps[0];
		const targetRow = row.rows[targetRowIdx];
		//expand target row if needed
		if (normalizedSlotTimesInRelationToDayStart.start < targetRow.start) targetRow.start = normalizedSlotTimesInRelationToDayStart.start;
		const targetRowEnd = normalizeEnd(targetRow.start, targetRow.end);
		if (normalizedSlotTimesInRelationToDayStart.end > targetRowEnd) targetRow.end = normalizedSlotTimesInRelationToDayStart.end;
		targetRow.shiftsAndGaps.push(newElement);
		/**
		 * We should make sure that the inner arrays are sorted by normalized start time
		 */
		targetRow.shiftsAndGaps.sort((a, b) => {
			return a.normalizedStartTime.diff(b.normalizedStartTime, "minutes").as("minutes");
		});
	}
}

/**
 * Insert a posted request into the given DayViewPostedRequest row.
 * Function will try to insert to the most upper inner row possible without overlapping existing requests,
 * in case we overlap move to a new inner row.
 *
 * @param row - The DayViewPostedRequest to update
 * @param start - Start time of the new request
 * @param end - End time of the new request
 * @param request - The request to add
 * @param date - The date for context
 */
export function upsertPostedRequestIntoDayViewPostedRequests({
	row,
	start,
	end,
	request,
	date,
	startHour,
}: {
	row: DayViewPostedRequest;
	start: DateTime;
	end: DateTime;
	request: OpenShiftRequest | ReplaceShiftRequest;
	date: DateTime;
	startHour: number;
}) {
	if (!row) {
		trackErrorEvent("posted_requests_upsert_no_row", {
			start: start?.toISO?.(),
			end: end?.toISO?.(),
			request,
		});
		return;
	}
	if (!start?.isValid || !end?.isValid || !date?.isValid) {
		trackErrorEvent("posted_requests_upsert_invalid_times", {
			start: start?.toISO?.(),
			end: end?.toISO?.(),
			date: date?.toISO?.(),
			request,
		});
		return;
	}

	const normalizedSlotTimesInRelationToDayStart = normalizeStartEndWithStartOfDay({ date, start, end, startHour });

	const newElement: DayViewPostedRequestElement = {
		value: request,
		normalizedStartTime: normalizedSlotTimesInRelationToDayStart.start,
		normalizedEndTime: normalizedSlotTimesInRelationToDayStart.end,
	};

	const noOverlaps: number[] = [];
	for (let i = 0; i < row.rows.length; i++) {
		const currentRowSlot = row.rows[i];
		const overlapsSomeElement = currentRowSlot.requests.some((existingRequest) => {
			// check for overlap with existingRequest
			return !(
				normalizedSlotTimesInRelationToDayStart.start >= existingRequest.normalizedEndTime ||
				normalizedSlotTimesInRelationToDayStart.end <= existingRequest.normalizedStartTime
			);
		});
		if (!overlapsSomeElement) {
			noOverlaps.push(i);
		}
	}

	if (noOverlaps.length === 0) {
		// all rows overlap, need to create a new row
		const newRowSlot: DayViewPostedRequestRowSlot = {
			start: normalizedSlotTimesInRelationToDayStart.start,
			end: normalizedSlotTimesInRelationToDayStart.end,
			requests: [newElement],
		};
		row.rows.push(newRowSlot);
	} else {
		// insert into the first non-overlapping row
		const targetRowIdx = noOverlaps[0];
		const targetRow = row.rows[targetRowIdx];
		// expand target row if needed
		if (normalizedSlotTimesInRelationToDayStart.start < targetRow.start) targetRow.start = normalizedSlotTimesInRelationToDayStart.start;
		const targetRowEnd = normalizeEnd(targetRow.start, targetRow.end);
		if (normalizedSlotTimesInRelationToDayStart.end > targetRowEnd) targetRow.end = normalizedSlotTimesInRelationToDayStart.end;
		targetRow.requests.push(newElement);
		// sort the requests by start time
		targetRow.requests.sort((a, b) => {
			return a.normalizedStartTime.diff(b.normalizedStartTime, "minutes").as("minutes");
		});
	}
}

export const locationAndRolesWidth = "200px";
export const minShiftWidth = "220px";
export const widthPercentToHideTextOnHover = 20;
export const hourJumpBetweenMarkers = 4;
export const shiftGaps = 2;
export const rowPadding = 1;

export const markerColor = "var(--inhouse-primary-dark)";
export const hourMarkerTypographySx = {
	fontWeight: 600,
	fontSize: "14px",
	lineHeight: "100%",
	letterSpacing: "0%",
	textAlign: "center",
	color: markerColor,
} as const satisfies SxProps;

export const colorForMissingBlock = "var(--inhouse-primary-alpha-08)";
export const colorForMissingBlockHighlighted = "#ff9019";
export const fontColorMissingBlock = "var(--inhouse-primary-dark)";
export const fontColorMissingBlockHighlighted = "white";
export const colorForPostedShiftBlock = "#B2DFDB";
export const colorForReplaceRequestBlock = "#E1BEE7";

export const blockTypographySx = {
	fontWeight: 600,
	fontSize: "12px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: "var(--inhouse-primary-dark)",
	px: 0.75,
	py: 0.5,
	height: "30px",
} as const satisfies SxProps;

export const locationTypographySx = {
	fontWeight: 500,
	fontSize: "14px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: "var(--inhouse-primary-dark)",
} as const satisfies SxProps;

export const rowGap = 0.5;

export const MAX_OVERLAP_MINUTES_FOR_SAME_ROW = 30;
